import KitchenLoader from './KitchenLoader.jsx'
import AgentRanBadge from './AgentRanBadge.jsx'
import { AGENT_LABELS } from '../lib/agents.js'

// HeroBriefCard — Phase 2 charcoal hero brief.
//
// Renders the AI brief at the top of every hub screen (Huddle, Stock,
// Schedule, Insights, Marketing) with a single visual contract:
//   - charcoal #2C2C2A wrapper, sentence-case eyebrow, white headline,
//     muted #D3D1C7 narrative
//   - phases: idle / generating / ready / empty / error
//   - never shows the generic blue spinner; KitchenLoader (tone=dark)
//     is the AI generation state in this app
//   - never shows raw error text; calm "couldn't pull today's brief"
//     fallback per CLAUDE.md
//   - never mentions AI cost on a customer surface
//
// Props:
//   tabKey   — 'huddle' | 'stock' | 'schedule' | 'insights' | 'marketing'
//   subKey   — optional sub-scope, e.g. 'receive', 'reorder', 'waste',
//              'catering' — used to label the eyebrow ("AI receive
//              brief") and pick the kitchen-themed loader messages
//   phase    — current lifecycle phase from useHeroBrief
//   brief    — payload object: { headline, narrative, cta?, bullets?,
//              recommended_action? }. Bullets + recommended_action
//              are only used by Huddle (richer than other tabs).
//   error    — error string (only relevant when phase==='error')
//   generatedAt — ISO timestamp string when phase==='ready'
//   onGenerate  — manual regenerate handler (for idle/error/refresh)
//   onCtaClick  — optional CTA chip handler

const TAB_LABEL = {
  huddle:    'morning',
  stock:     'stock',
  schedule:  'schedule',
  insights:  'weekly',
  marketing: 'marketing',
}

const SUB_LABEL = {
  receive:  'receive',
  reorder:  'reorder',
  waste:    'waste',
  catering: 'catering',
}

const LOADER_MESSAGES_BY_TAB = {
  huddle: [
    "Counting today's pieces…",
    'Looking at sales and waste…',
    'Plating your morning brief…',
  ],
  stock: [
    'Counting on-hand…',
    'Cross-checking par levels…',
    'Plating your stock brief…',
  ],
  schedule: [
    'Looking at coverage…',
    'Pulling upcoming catering…',
    'Drafting your schedule brief…',
  ],
  insights: [
    'Comparing this week to last…',
    'Pulling labor and food cost…',
    'Plating your weekly brief…',
  ],
  marketing: [
    'Reading your drafts…',
    'Checking last campaign…',
    'Drafting your marketing brief…',
  ],
}

// Sub-scoped briefs inherit the parent tab's loader messages — the
// hub messages are general enough to read well for any sub.
function loaderMessagesFor(tabKey) {
  return LOADER_MESSAGES_BY_TAB[tabKey] || LOADER_MESSAGES_BY_TAB.huddle
}

function eyebrowLabel(tabKey, subKey) {
  if (subKey && SUB_LABEL[subKey]) {
    return `AI ${SUB_LABEL[subKey]} brief`
  }
  // Ship 8 — the Huddle hero is framed as the morning manager's brief.
  if (tabKey === 'huddle' && !subKey) {
    return AGENT_LABELS.huddleBrief
  }
  return `AI ${TAB_LABEL[tabKey] || 'daily'} brief`
}

// Sentence-case "9:42 am" — no seconds, lowercase meridiem.
function formatGeneratedAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const s = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  // Lowercase the meridiem to match sentence case.
  return `Generated ${s.replace(/\bAM\b/, 'am').replace(/\bPM\b/, 'pm')}`
}

export default function HeroBriefCard({
  tabKey,
  subKey,
  phase,
  brief,
  error,
  generatedAt,
  onGenerate,
  onCtaClick,
}) {
  const eyebrow = eyebrowLabel(tabKey, subKey)
  const loaderMessages = loaderMessagesFor(tabKey)

  return (
    <section
      className="relative mx-4 mt-4 rounded-2xl bg-charcoal-hero p-4 shadow-sm"
      aria-label={eyebrow}
    >
      {/* Top row — eyebrow on left, refresh affordance on right when
          phase=ready. The refresh pill sits at the corner so it
          doesn't compete with the headline for the user's attention.
          FIX H7: refresh button is disabled while a generation is in
          flight, with a visible disabled state and aria-busy. */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-medium text-charcoal-text-secondary">
          {eyebrow}
        </div>
        <div className="flex items-baseline gap-2">
          {/* Ship 8 — "Agent ran · …" badge on the Huddle morning brief
              (the only badged HeroBriefCard surface). Renders nothing
              without a usable generated_at. */}
          {tabKey === 'huddle' && phase === 'ready' && (
            <AgentRanBadge generatedAt={generatedAt} tone="dark" />
          )}
          {phase === 'ready' && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={phase === 'generating'}
              aria-busy={phase === 'generating'}
              className="text-[11px] text-charcoal-text-secondary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-charcoal-text-secondary"
              aria-label="Refresh brief"
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Phase: idle. Charcoal must not break — no blue button.
          Underline link in the same tone palette. */}
      {phase === 'idle' && (
        <div className="mt-2">
          <p className="text-[12px] text-charcoal-text-tertiary leading-relaxed">
            Tap to generate today's brief.
          </p>
          <button
            type="button"
            onClick={onGenerate}
            className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
          >
            Generate
          </button>
        </div>
      )}

      {/* Phase: generating. KitchenLoader, tone=dark, sm row. */}
      {phase === 'generating' && (
        <div className="mt-2">
          <KitchenLoader
            align="row"
            size="sm"
            tone="dark"
            messages={loaderMessages}
          />
        </div>
      )}

      {/* Phase: ready. Headline + narrative + (huddle-only) bullets +
          recommended action + optional CTA chip + bottom-right
          timestamp. */}
      {phase === 'ready' && brief && (
        <>
          <div className="mt-2 text-[16px] sm:text-[18px] font-medium leading-snug text-white">
            {brief.headline}
          </div>
          {brief.narrative && (
            <p className="mt-2 text-[12px] leading-relaxed text-charcoal-text-tertiary">
              {brief.narrative}
            </p>
          )}

          {/* Huddle-only: bullets + recommended_action. The plan calls
              for these to render in the Huddle variant only — other
              tabs use just headline + narrative. We render both
              optionally so the same component supports both shapes. */}
          {Array.isArray(brief.bullets) && brief.bullets.length > 0 && (
            <ul className="mt-3 space-y-1.5 text-[12px] leading-relaxed text-charcoal-text-tertiary">
              {brief.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-charcoal-text-secondary"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {(brief.recommended_action || brief.recommendedAction) && (
            <div className="mt-3 rounded-md bg-white/5 px-3 py-2 text-[12px] text-charcoal-text-tertiary">
              <span className="text-[11px] font-medium text-charcoal-text-secondary">
                Recommended
              </span>
              <span className="ml-2 text-white/90">
                {brief.recommended_action || brief.recommendedAction}
              </span>
            </div>
          )}

          {brief.cta && brief.cta.label && (
            <button
              type="button"
              onClick={() => {
                if (typeof brief.cta.onClick === 'function') brief.cta.onClick()
                if (typeof onCtaClick === 'function') onCtaClick(brief.cta)
              }}
              className="mt-3 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              {brief.cta.label}
            </button>
          )}

          {generatedAt && (
            <div className="mt-3 text-right text-[10px] text-charcoal-text-secondary">
              {formatGeneratedAt(generatedAt)}
            </div>
          )}
        </>
      )}

      {/* Phase: empty. Deterministic friendly nudge — model wasn't
          called (tiny-data guard) or model call timed out.
          FIX M3: when brief.retryable === true (e.g. timeout fallback),
          show a Refresh button so the user can try again. When
          retryable is falsy (tiny-data), no button — more data needs
          to land before retry would help. */}
      {phase === 'empty' && brief && (
        <>
          <div className="mt-2 text-[16px] sm:text-[18px] font-medium leading-snug text-white">
            {brief.headline || "We couldn't analyze enough data yet."}
          </div>
          {brief.narrative && (
            <p className="mt-2 text-[12px] leading-relaxed text-charcoal-text-tertiary">
              {brief.narrative}
            </p>
          )}
          {brief.retryable === true && (
            <button
              type="button"
              onClick={onGenerate}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Refresh
            </button>
          )}
        </>
      )}

      {/* Phase: error. Calm muted line + retry. Never raw error text. */}
      {phase === 'error' && (
        <div className="mt-2">
          <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
            Couldn't pull today's brief — tap to try again.
          </p>
          <button
            type="button"
            onClick={onGenerate}
            className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
          >
            Retry
          </button>
        </div>
      )}
    </section>
  )
}
